<template>
  <div class="image-preview-wrapper">
    <!-- 缩略图展示 -->
    <img
      :src="thumbnailUrl"
      :class="['thumbnail-image', customClass]"
      :style="thumbnailStyle"
      @click="handlePreview"
      @error="handleImageError"
    />

    <!-- 原图预览 Modal -->
    <a-modal
      :visible="previewVisible"
      :footer="null"
      @cancel="handleClose"
      :width="previewWidth"
      centered
      :z-index="1000"
      class="image-preview-modal"
    >
      <div class="preview-container">
        <!-- 多图浏览时的导航按钮 -->
        <div v-if="imageList && imageList.length > 1" class="preview-nav">
          <a-icon
            type="left"
            class="nav-btn nav-btn-left"
            :class="{ disabled: localCurrentIndex === 0 }"
            @click="handlePrev"
          />
          <a-icon
            type="right"
            class="nav-btn nav-btn-right"
            :class="{ disabled: localCurrentIndex === imageList.length - 1 }"
            @click="handleNext"
          />
          <div class="preview-counter">{{ localCurrentIndex + 1 }} / {{ imageList.length }}</div>
        </div>

        <!-- 原图展示 -->
        <img
          v-if="previewType === 'image'"
          :src="originalUrl"
          class="preview-original-image"
          @load="handleImageLoad(originalUrl)"
          @error="handleOriginalImageError"
        />
        <video
          v-else-if="previewType === 'video'"
          :src="originalUrl"
          class="preview-original-video"
          controls
          autoplay
        />

        <!-- 加载状态 -->
        <div v-if="imageLoading" class="image-loading">
          <a-spin size="large" />
        </div>
      </div>
    </a-modal>
  </div>
</template>

<script>
export default {
  name: 'ImagePreview',
  props: {
    // 图片URL（必需）
    src: {
      type: String,
      required: true,
    },
    // 图片列表（可选，用于多图浏览）
    imageList: {
      type: Array,
      default: null,
    },
    // 当前图片在列表中的索引（配合imageList使用）
    currentIndex: {
      type: Number,
      default: 0,
    },
    // 缩略图尺寸（宽高，默认90px）
    thumbnailSize: {
      type: Number,
      default: 90,
    },
    // 自定义缩略图样式类
    customClass: {
      type: String,
      default: '',
    },
    // 自定义缩略图样式
    thumbnailStyle: {
      type: Object,
      default: () => ({}),
    },
    // 预览Modal宽度
    previewWidth: {
      type: Number,
      default: 800,
    },
    // 是否禁用预览
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      previewVisible: false,
      imageLoading: false,
      localCurrentIndex: 0,
      loadedImageList: [],
    }
  },
  computed: {
    // 当前显示的图片URL
    currentImageUrl() {
      if (this.imageList && this.imageList.length > 0) {
        const index = this.previewVisible ? this.localCurrentIndex : this.currentIndex
        const item = this.imageList[index]
        return typeof item === 'string' ? item : item?.url || item?.src || ''
      }
      return this.src
    },
    // 缩略图URL（使用OSS压缩，固定展示入口那一张，不随预览左右切换而改变）
    thumbnailUrl() {
      // 如果有图片列表，则缩略图始终使用 currentIndex 对应的那一张
      if (this.imageList && this.imageList.length > 0) {
        const index = Math.min(this.currentIndex, this.imageList.length - 1)
        const item = this.imageList[index]
        const url = typeof item === 'string' ? item : item?.url || item?.src || ''
        return this.getThumbnailUrl(url || this.src)
      }
      // 否则使用单图 src
      return this.getThumbnailUrl(this.src)
    },
    // 原图URL
    originalUrl() {
      return this.currentImageUrl
    },
    // 预览类型（图片或视频）
    previewType() {
      const url = this.currentImageUrl
      if (/\.(mp4|webm|ogg|mov|avi)$/i.test(url)) {
        return 'video'
      }
      return 'image'
    },
  },
  watch: {
    currentIndex: {
      handler(newVal) {
        this.localCurrentIndex = newVal
      },
      immediate: true,
    },
    imageList: {
      handler() {
        if (this.imageList && this.imageList.length > 0) {
          this.localCurrentIndex = Math.min(this.currentIndex, this.imageList.length - 1)
        }
      },
      immediate: true,
    },
  },
  methods: {
    // 获取OSS缩略图URL
    getThumbnailUrl(url) {
      if (!url || typeof url !== 'string') return url

      // 判断是否是视频，视频不需要压缩
      if (/\.(mp4|webm|ogg|mov|avi)$/i.test(url)) return url

      // 判断是否是OSS URL（通常包含oss相关域名或路径）
      const isOssUrl =
        /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(url) || /oss/i.test(url) || /aliyuncs/i.test(url)

      if (!isOssUrl) return url

      // 检查URL是否已经有参数
      const hasParams = url.includes('?')
      const separator = hasParams ? '&' : '?'

      // 添加OSS图片处理参数：固定宽高，使用填充模式
      return `${url}${separator}x-oss-process=image/resize,m_fill,w_${this.thumbnailSize},h_${this.thumbnailSize}`
    },
    // 打开预览
    handlePreview() {
      if (this.disabled) return

      this.localCurrentIndex = this.currentIndex
      this.previewVisible = true
      if (!this.loadedImageList.includes(this.originalUrl)) {
        this.imageLoading = true
      }
    },
    // 关闭预览
    handleClose() {
      this.previewVisible = false
      this.imageLoading = false
    },
    // 上一张
    handlePrev() {
      if (this.localCurrentIndex > 0) {
        this.localCurrentIndex--
        if (!this.loadedImageList.includes(this.originalUrl)) {
          this.imageLoading = true
        }
      }
    },
    // 下一张
    handleNext() {
      if (this.imageList && this.localCurrentIndex < this.imageList.length - 1) {
        this.localCurrentIndex++
        if (!this.loadedImageList.includes(this.originalUrl)) {
          this.imageLoading = true
        }
      }
    },
    // 图片加载完成
    handleImageLoad(src) {
      this.loadedImageList.push(src)
      this.imageLoading = false
    },
    // 缩略图加载错误
    handleImageError(e) {
      // 如果缩略图加载失败，尝试显示原图
      if (e.target.src !== this.currentImageUrl) {
        e.target.src = this.currentImageUrl
      }
    },
    // 原图加载错误
    handleOriginalImageError() {
      this.imageLoading = false
      this.$message.error(this.$t('图片加载失败'))
    },
  },
}
</script>

<style scoped>
.image-preview-wrapper {
  display: inline-block;
}

.thumbnail-image {
  cursor: pointer;
  transition: all 0.2s;
  border-radius: 4px;
  /* border: 1px solid #e5e5e5; */
  display: block;
}

.thumbnail-image:hover {
  opacity: 0.8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.preview-container {
  position: relative;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-nav {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 10;
}

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  font-size: 24px;
  color: rgba(255, 255, 255, 0.9);
  cursor: pointer;
  pointer-events: all;
  background: rgba(0, 0, 0, 0.5);
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: all 0.3s;
}

.nav-btn:hover:not(.disabled) {
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
}

.nav-btn.disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.nav-btn-left {
  left: 20px;
}

.nav-btn-right {
  right: 20px;
}

.preview-counter {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 14px;
  pointer-events: none;
}

.preview-original-image {
  max-width: 100%;
  object-fit: contain;
  display: block;
}

.preview-original-video {
  max-width: 100%;
  display: block;
}

.image-loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 5;
}

/* Modal 样式优化 */
.image-preview-modal :deep(.ant-modal-body) {
  padding: 24px;
}

.image-preview-modal :deep(.ant-modal-close) {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(0, 0, 0, 0.5);
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.image-preview-modal :deep(.ant-modal-close:hover) {
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
}
</style>
